import {
  Box,
  Typography,
  Chip,
  Grid,
  Divider,
} from '@mui/material'
import {
  Business as BusinessIcon,
  Favorite as FavoriteIcon,
  Work as WorkIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material'
import { Profile, GENDER_LABELS, RoomSubInfoItem } from '@/types/profile'
import { ProfileImageAvatar } from '@/components/ProfileImageAvatar'
import { ROOM_OWNER_ROLE_LABEL, ROOM_OWNER_DESCRIPTION } from '@/utils/roomOwner'

/** サブ情報の表示値を統一フォーマットに変換する（真偽値は「<項目名>です」「<項目名>でない」で統一） */
const formatSubInfoValue = (sub: RoomSubInfoItem) => {
  if (sub.propertyType === 'checkbox') {
    const isTrue = sub.subValue === 'true' || sub.subValue === '1'
    return isTrue ? `${sub.propertyName}です` : `${sub.propertyName}でない`
  }
  return sub.subValue || '未入力'
}

/** プロフィールタイプに応じた詳細ビュー。Profile（共通モデル）を直接受け取る。 */
export const ProfileDetailView = ({ profile }: { profile: Profile }) => {

  // ── ヘッダー部分（タイプ別） ──────────────────────────
  const renderDetailHeader = () => {
    if (profile.type === 'business') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <ProfileImageAvatar userId={profile.id} type={profile.type} fallbackSrc={profile.photoUrl} sx={{ width: 100, height: 100 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>{profile.name}</Typography>
            <Typography variant="body1" color="text.secondary">{profile.companyName}</Typography>
            <Chip icon={<BusinessIcon />} label={profile.position} size="small" sx={{ mt: 1 }} />
          </Box>
        </Box>
      )
    }
    if (profile.type === 'dating') {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <ProfileImageAvatar userId={profile.id} type={profile.type} fallbackSrc={profile.photoUrl} sx={{ width: 100, height: 100 }} />
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography variant="h5" fontWeight={700}>{profile.name}</Typography>
              <Chip label={`No.${profile.userNumber}`} size="small" color="primary" variant="outlined" />
            </Box>
            <Typography variant="body1" color="text.secondary">
              {profile.age}歳 / {GENDER_LABELS[profile.gender]}
            </Typography>
            <Chip
              icon={<FavoriteIcon />}
              label={profile.occupation}
              size="small"
              sx={{ mt: 1, bgcolor: '#fce4ec' }}
            />
          </Box>
        </Box>
      )
    }
    // job
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <ProfileImageAvatar userId={profile.id} type={profile.type} fallbackSrc={profile.photoUrl} sx={{ width: 100, height: 100 }} />
        <Box>
          <Typography variant="h5" fontWeight={700}>{profile.name}</Typography>
          {profile.companyName && (
            <Typography variant="body1" color="text.secondary">{profile.companyName}</Typography>
          )}
          <Chip
            icon={<WorkIcon />}
            label={profile.lastPosition}
            size="small"
            sx={{ mt: 1, bgcolor: '#e8f5e9' }}
          />
        </Box>
      </Box>
    )
  }

  // ── タイプ別のメインフィールド ────────────────────────
  const renderTypeFields = () => {
    if (profile.type === 'business') {
      return (
        <Grid item xs={12}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>ビジネス内容</Typography>
          <Typography variant="body1">{profile.businessContent}</Typography>
        </Grid>
      )
    }
    if (profile.type === 'dating') {
      return (
        <>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>年収</Typography>
            <Typography variant="body1" fontWeight={500}>{profile.annualIncome}</Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>希望の子供の人数</Typography>
            <Typography variant="body1" fontWeight={500}>{profile.wantChildren || '未設定'}</Typography>
          </Grid>
        </>
      )
    }
    // job
    return (
      <>
        <Grid item xs={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>最終学歴</Typography>
          <Typography variant="body1" fontWeight={500}>{profile.education}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>学校名</Typography>
          <Typography variant="body1" fontWeight={500}>{profile.schoolName}</Typography>
        </Grid>
        {(profile.age || profile.gender) && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>基本情報</Typography>
            <Typography variant="body1">
              {profile.age && `${profile.age}歳`}
              {profile.age && profile.gender && ' / '}
              {profile.gender && GENDER_LABELS[profile.gender]}
            </Typography>
          </Grid>
        )}
      </>
    )
  }

  // ── マッチング希望ラベル ──────────────────────────────
  const wantToMatchLabel = profile.type === 'dating' ? 'マッチしたい相手' : 'マッチしたい業種'
  const notWantToMatchLabel = profile.type === 'dating' ? 'マッチしたくない相手' : 'マッチしたくない業種'

  return (
    <>
      {/* 主催者はプロフィール未登録の場合があり、タイプ別項目が空欄で並ぶ。
          「管理者(主催者)である」ことが伝わる表示を先頭に出す。 */}
      {profile.isRoomOwner && (
        <Box sx={{ mb: 2, p: 2, bgcolor: '#fff4e5', borderRadius: 2, border: '1px solid', borderColor: 'warning.main' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AdminIcon fontSize="small" sx={{ color: 'warning.dark' }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'warning.dark' }}>
              {ROOM_OWNER_ROLE_LABEL}
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {ROOM_OWNER_DESCRIPTION}
          </Typography>
        </Box>
      )}

      {renderDetailHeader()}

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        {/* タイプ別メインフィールド */}
        {renderTypeFields()}

        {/* PR（共通） */}
        {profile.pr && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>PR</Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>{profile.pr}</Typography>
          </Grid>
        )}

        {/* マッチング希望 */}
        {profile.wantToMatch && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>{wantToMatchLabel}</Typography>
            <Typography variant="body1" color="primary.main">{profile.wantToMatch}</Typography>
          </Grid>
        )}

        {profile.notWantToMatch && (
          <Grid item xs={12}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>{notWantToMatchLabel}</Typography>
            <Typography variant="body1" color="error.main">{profile.notWantToMatch}</Typography>
          </Grid>
        )}

        {/* 連絡先（共通） */}
        {(profile.contactEmail || profile.contactPhone) && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>連絡先</Typography>
            {profile.contactEmail && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <EmailIcon fontSize="small" color="action" />
                <Typography variant="body2">{profile.contactEmail}</Typography>
              </Box>
            )}
            {profile.contactPhone && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <PhoneIcon fontSize="small" color="action" />
                <Typography variant="body2">{profile.contactPhone}</Typography>
              </Box>
            )}
          </Grid>
        )}

        {/* ルームサブ情報 */}
        {profile.subInfos && profile.subInfos.length > 0 && (
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>追加情報</Typography>
            {profile.subInfos.map((sub) => (
              <Box key={sub.roomSubInfoId} sx={{ mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{sub.propertyName}</Typography>
                <Typography variant="body2" fontWeight={500}>{formatSubInfoValue(sub)}</Typography>
              </Box>
            ))}
          </Grid>
        )}
      </Grid>
    </>
  )
}
